import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Snackbar,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import { IReduxState } from 'interfaces/IReduxState'
import { useSelector } from 'react-redux'
import API from '@aws-amplify/api'

interface ClosePurchaseOrderDialogProps {
  open: boolean
  onClose: () => void
  purchaseOrder: any
  onSubmit: (notes: string, items: any[]) => void
}

export default function ClosePurchaseOrderDialog({
  open,
  onClose,
  purchaseOrder,
  onSubmit,
}: ClosePurchaseOrderDialogProps) {
  const [notes, setNotes] = React.useState('')
  const [items, setItems] = React.useState<any[]>([])
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)

  React.useEffect(() => {
    if (open && purchaseOrder) {
      setItems(
        purchaseOrder.purchaseOrderItems?.map((item: any) => ({
          ...item,
          closedNotes: item.closedNotes || '',
        })) || []
      )
    }
  }, [open, purchaseOrder])

  const handleItemNoteChange = (idx: number, value: string) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, closedNotes: value } : it))
    )
  }

  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const handleClosePurchaseOrder = async () => {
    if (!purchaseOrder) return
    setIsSubmitting(true)
    try {
      await API.post(
        'products',
        `/purchaseOrders/closePurchaseOrder/${purchaseOrder.purchaseOrderId}`,
        {
          body: {
            closedNotes: notes,
            items: items.map((item) => ({
              itemId: item.itemId,
              closedNotes: item.closedNotes,
            })),
          },
        }
      )
      setAlertMessage('Berhasil menutup PO')
      setIsSuccessSnackbarOpen(true)
      setIsSubmitting(false)
      onSubmit(notes, items)
      onClose()
    } catch (error) {
      setAlertMessage('Gagal menutup PO')
      setIsFailureSnackbarOpen(true)
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Tutup Purchase Order</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="subtitle1">
            <b>PO Number:</b> {purchaseOrder?.poNumber}
          </Typography>
          <Typography variant="subtitle1">
            <b>PO Date:</b> {purchaseOrder?.poDate?.toLocaleDateString() || '-'}
          </Typography>
          <Typography variant="subtitle1">
            <b>Supplier:</b> {purchaseOrder?.supplier}
          </Typography>
        </Box>
        <TableContainer component={Paper} style={{ marginBottom: 16 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kode Item</TableCell>
                <TableCell>Qty Purchased</TableCell>
                <TableCell>Qty Received</TableCell>
                <TableCell>Adjusted Qty</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Total Unit Price</TableCell>
                <TableCell>Notes Kenapa di Tutup untuk Item Ini</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => {
                const adjustedQty =
                  (item.quantityPurchased ?? 0) - (item.quantityReceived ?? 0)
                const totalUnitPrice = adjustedQty * (item.unitPrice ?? 0)
                return (
                  <TableRow key={item.itemId || idx}>
                    <TableCell>{item.itemId}</TableCell>
                    <TableCell>{item.quantityPurchased ?? 0}</TableCell>
                    <TableCell>{item.quantityReceived ?? 0}</TableCell>
                    <TableCell>{adjustedQty}</TableCell>
                    <TableCell>{`Rp.${new Intl.NumberFormat('id-ID').format(
                      Number(item.unitPrice ?? 0)
                    )}`}</TableCell>
                    <TableCell>{`Rp.${new Intl.NumberFormat('id-ID').format(
                      Number(totalUnitPrice)
                    )}`}</TableCell>
                    <TableCell>
                      <TextField
                        label="Notes Kenapa di Tutup untuk Item Ini"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={item.closedNotes || ''}
                        onChange={(e) =>
                          handleItemNoteChange(idx, e.target.value)
                        }
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box mb={2}>
          <TextField
            label="Notes PO Kenapa di Tutup"
            variant="outlined"
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ marginBottom: 16 }}
          />
          <Typography variant="subtitle1">
            <b>Total Adjustment:</b>{' '}
            {`Rp.${new Intl.NumberFormat('id-ID').format(
              items.reduce(
                (sum, item) =>
                  sum +
                  ((item.quantityPurchased ?? 0) -
                    (item.quantityReceived ?? 0)) *
                    (item.unitPrice ?? 0),
                0
              ) || 0
            )}`}
          </Typography>
          <Typography variant="subtitle1">
            <b>Closed By:</b> {user.username || '-'}
          </Typography>
          <Typography variant="subtitle1">
            <b>Tanggal Tutup PO:</b> {new Date().toLocaleDateString('id-ID')}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleClosePurchaseOrder}
            disabled={isSubmitting}
          >
            Tutup PO
          </Button>
        </Box>
        <Snackbar
          open={isSuccessSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setIsSuccessSnackbarOpen(false)}
        >
          <MuiAlert severity="success" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
        <Snackbar
          open={isFailureSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setIsFailureSnackbarOpen(false)}
        >
          <MuiAlert severity="error" elevation={6} variant="filled">
            {alertMessage}
          </MuiAlert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  )
}
